/**
 * /s/:token — real landing page (Task 13, replacing the Task 11 placeholder).
 *
 * Rendering rules:
 *   - Multi-item share, no visitor cookie → identify form gates the grid
 *     (spec §"decision 7 — guest identify timing" for multi-item shares).
 *   - Multi-item share, cookie present → grid landing directly.
 *   - Single-asset shares — handled upstream in ShareEntryPage (auto-drills
 *     to /s/:token/v/:videoId), so we never render for those.
 *
 * Consumer is share-entry.tsx after it resolves the token. Kept as a separate
 * page component to keep share-entry lean (pure resolver + branch dispatcher).
 */

import { Link } from "react-router-dom";
import { FileImage, Folder as FolderIcon, MessageSquare, Music2, Share2, Video as VideoIcon } from "lucide-react";
import { ShareBrandHeader, PoweredByEditlock, hasCustomBrand } from "@/components/shares/share-brand-header";
import { ShareIdentifyForm } from "@/components/shares/share-identify-form";
import { brandAccentStyle } from "@/lib/brand";
import { formatDurationChip } from "@/components/projects/video-card";
import type { ShareLandingData, ShareLandingItem } from "@/hooks/use-share";

type Props = {
  token: string;
  data: ShareLandingData;
  /** Task 3 — team preview flag, propagated onto every internal share link. */
  preview?: boolean;
};

function AssetIcon({ assetType, className }: { assetType?: string; className?: string }) {
  if (assetType === "audio") return <Music2 className={className} strokeWidth={1.5} />;
  if (assetType === "image") return <FileImage className={className} strokeWidth={1.5} />;
  return <VideoIcon className={className} strokeWidth={1.5} />;
}

export function ShareLandingPage({ token, data, preview = false }: Props) {
  const gateOnIdentify = !data.skipForm;
  const pv = preview ? "?preview=1" : "";

  return (
    <div className="min-h-screen bg-background text-foreground" style={brandAccentStyle(data.brand?.accent)}>
      <ShareBrandHeader brand={data.brand} />

      {gateOnIdentify ? (
        <main className="mx-auto max-w-4xl px-6 py-16">
          <ShareIdentifyForm token={token} adminName={data.adminName} />
        </main>
      ) : (
        <ShareGrid token={token} data={data} pv={pv} />
      )}

      {hasCustomBrand(data.brand) && <PoweredByEditlock />}
    </div>
  );
}

function ShareGrid({ token, data, pv }: { token: string; data: ShareLandingData; pv: string }) {
  const title =
    data.share.name ||
    (data.items.length === 1
      ? data.items[0].type === "video"
        ? data.items[0].title
        : data.items[0].name
      : `${data.items.length} items`);

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      {/* Header row — Frame.io-ish "shared by" line + title + expiry. */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            {data.adminName ? `Shared by ${data.adminName}` : "Shared review"}
          </div>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
            <Share2 className="h-6 w-6 text-muted-foreground" strokeWidth={1.75} />
            {title}
          </h1>
          {data.share.expiresAt ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Access expires {new Date(data.share.expiresAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {data.items.map((item) => (
          <ShareCard key={`${item.type}:${item.id}`} token={token} item={item} pv={pv} />
        ))}
      </div>
    </main>
  );
}

function ShareCard({ token, item, pv }: { token: string; item: ShareLandingItem; pv: string }) {
  if (item.type === "folder") {
    const label =
      item.folderCount + item.videoCount === 0
        ? "Empty"
        : `${item.folderCount ? `${item.folderCount} folder${item.folderCount === 1 ? "" : "s"}` : ""}${item.folderCount && item.videoCount ? " · " : ""}${item.videoCount ? `${item.videoCount} asset${item.videoCount === 1 ? "" : "s"}` : ""}`;
    return (
      <Link
        to={`/s/${encodeURIComponent(token)}/f/${encodeURIComponent(item.id)}${pv}`}
        className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-secondary/40"
      >
        <div className="grid aspect-video place-items-center rounded-lg bg-secondary/60 text-muted-foreground">
          <FolderIcon className="h-8 w-8" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground">{item.name}</div>
          <div className="truncate text-xs text-muted-foreground">{label}</div>
        </div>
      </Link>
    );
  }

  // Video card — inline thumbnail preview.
  const durationText = formatDurationChip(item.durationSeconds, item.assetType);
  const commentCount = typeof item.commentCount === "number" ? item.commentCount : 0;
  const hasComments = commentCount > 0;
  return (
    <Link
      to={`/s/${encodeURIComponent(token)}/v/${encodeURIComponent(item.id)}${pv}`}
      className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-secondary/40"
    >
      <div className="relative grid aspect-video place-items-center overflow-hidden rounded-lg bg-secondary/60">
        {item.assetType === "video" && item.cdnUrl ? (
          <video src={item.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
        ) : item.assetType === "image" && item.cdnUrl ? (
          <img src={item.cdnUrl} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <AssetIcon assetType={item.assetType} className="h-7 w-7 text-muted-foreground" />
        )}
        {item.versionCount > 1 && (
          <span className="absolute right-1.5 top-1.5 rounded-md bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground">
            V{item.versionCount}
          </span>
        )}
        {hasComments && (
          <span
            className="pointer-events-none absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white"
            title={`${commentCount} comment${commentCount === 1 ? "" : "s"}`}
          >
            <MessageSquare className="h-3 w-3" strokeWidth={2} />
            <span className="tabular-nums">{commentCount}</span>
          </span>
        )}
        {durationText && (
          <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
            {durationText}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{item.title}</div>
        {item.assetType && (
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            {item.assetType}
          </div>
        )}
      </div>
    </Link>
  );
}
